import { runInAction, makeAutoObservable, toJS } from "mobx";
import { userStore } from "./userStore";
import type { User } from "graphql/generated";
import { notificationStore } from "./notificationStore";
import type { GraphQLError } from "graphql";

export class AuthStore {
  errors?:
    | GraphQLError
    | ReadonlyArray<GraphQLError>
    | Pick<GraphQLError, "message">;
  inProgress = false;
  pageLoaded = false;
  forgotPassword = false;
  resendEmail = false;
  emailVerified = false;
  isResetPasswordPending = false;
  differentUser = false;
  operationTokens = {} as Record<string, string>;
  mfaAuthCode = "";
  mfaTrustToken: string | null = null; //token returned on successful MFA challenge.  Don't store in web storage!
  mfaChallengeToken = null as unknown as string; //token to verify client sending request is the same that passed mfa challenge
  mfaSecret = ""; //secret generated to add MFA: goes to authenticator app
  twoFactorId = ""; //verification code to send with authenticator code on challenge
  recoveryCodes: string[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  get needsMfaChallenge() {
    return userStore.hasMfa && !this.mfaTrustToken;
  }

  setProgress = (status: boolean) => {
    runInAction(() => (this.inProgress = status));
  };

  setAccount = ({
    id,
    name,
    email,
    phone,
  }: Pick<User, "id" | "email"> & Partial<Pick<User, "name" | "phone">>) => {
    userStore.updateId(id);

    // Checks to see if user is logging in with a
    // different user
    if (userStore.currentEmail !== email) {
      this.setDifferentUser(true);
    } else {
      this.setDifferentUser(false);
    }
    userStore.updateEmail(email);
    userStore.updatePhone(!!phone ? phone : "");
    userStore.updateName(!!name ? name : "");
  };

  setError = (errors: AuthStore["errors"] | string) => {
    runInAction(() => {
      this.errors = Array.isArray(errors)
        ? errors.length > 1
          ? errors
          : errors[0]
        : errors;
      this.setProgress(false);
    });
  };

  setForgotPassword = (status: boolean) => {
    runInAction(() => {
      this.forgotPassword = status;
    });
  };

  setResendEmail = (status: boolean) => {
    runInAction(() => {
      this.resendEmail = status;
    });
  };

  setResetPasswordStatus = (status: boolean) => {
    runInAction(() => {
      this.isResetPasswordPending = status;
    });
  };

  setDifferentUser = (status: boolean) => {
    runInAction(() => {
      this.differentUser = status;
    });
  };

  resetEmailFlow = () => {
    this.setForgotPassword(false);
    this.setResetPasswordStatus(false);
    this.setResendEmail(false);
    this.resetError();
    this.setProgress(false);
  };
  resetError = () => {
    runInAction(() => {
      this.errors = undefined;
    });
  };
  bindOperationToken = ({
    operationName,
    token,
  }: {
    operationName: string;
    token: string;
  }) => {
    runInAction(() => {
      this.operationTokens[operationName] = toJS(token);
    });
  };

  addMfaVariables = <T extends object>(
    variables: T,
  ): T & { trustToken?: string; trustChallenge?: string } => {
    if (userStore.hasMfa) {
      if (this.mfaTrustToken && this.mfaChallengeToken) {
        return {
          ...variables,
          trustToken: this.mfaTrustToken,
          trustChallenge: this.mfaChallengeToken,
        };
      } else {
        notificationStore.showErrorToaster(
          "You cannot change your password without first verifying using two-factor authentication",
        );
      }
    }
    return variables;
  };

  setMfaTrustToken = (trustToken: string | null) => {
    runInAction(() => {
      this.mfaTrustToken = trustToken;
    });
  };

  generateMfaChallengeToken = ({ shouldReset = false } = {}) => {
    runInAction(() => {
      this.mfaChallengeToken = shouldReset ? "" : crypto.randomUUID();
    });
  };

  setRecoveryCodes = (recoveryCodes: string[]) => {
    runInAction(() => {
      this.recoveryCodes = recoveryCodes;
    });
  };

  removeRecoveryCodes = () => {
    runInAction(() => {
      this.recoveryCodes = [];
    });
  };

  setMfaSecret = ({ secret = "" } = {}) => {
    runInAction(() => {
      this.mfaSecret = secret;
    });
  };

  clearTwoFactorId = () => {
    runInAction(() => {
      this.twoFactorId = "";
    });
  };

  setTwoFactorId = (twoFactorId: string) => {
    runInAction(() => {
      this.twoFactorId = twoFactorId;
    });
  };

  clearMfaChallenge = () => {
    runInAction(() => {
      this.mfaTrustToken = null;
      this.generateMfaChallengeToken({ shouldReset: true });
    });
  };
}

export const authStore = new AuthStore();
